"use client";

// The history body: the ONLY component in this feature that calls
// useRelayOrders for a standalone (non-deposit) view. Task 12's simpler
// sibling of RelayDepositContent.tsx (Task 11) -- no wizard step, no
// catalog/target picking, no wallet-injected send/write capabilities. Just:
// resolve an address (recipient, falling back to walletAddress), build/accept
// a client (`client` wins, else the effective base URL -- `apiBaseUrl`, or the
// `environment`-keyed default map when `apiBaseUrl` isn't passed -- builds one
// via createRelayClient; see resolveRelayApiBaseUrl), and render <OrdersList>
// against useRelayOrders's result -- or an empty-state notice when there is no
// valid address to look up at all. The catalog is loaded alongside the list:
// order amounts are smallest-unit integer strings and cannot be displayed
// correctly until their token decimals are known.
import * as React from "react";

import { TrackingBoundary, trackDappUiFunnel } from "../../../lib/sentry/index";
import { useRelayOrders } from "../hooks/useRelayOrders";
import { resolveRelayApiBaseUrl } from "../lib/api-base";
import { createRelayClient } from "../lib/client";
import type { Catalog } from "../lib/types";
import { isAddress } from "../lib/units";
import { useRelayHistoryContext } from "../providers/relay-history-provider";
import { useRelayShellContext } from "../providers/relay-shell-provider";
import { HistoryEmptyState, OrdersList } from "./OrdersList";
import { ShellContent } from "./shell/ResponsiveShell";
import "../styles/component.css";

export interface RelayHistoryContentProps {
  /** Extra class name(s) for the dialog/drawer content element. Falls back to
   * `<RelayHistory>`'s own `className`. */
  className?: string;
}

/**
 * `<RelayHistory.Content>` — the modal/drawer surface. Like
 * `RelayDepositContent`, this is a child of `ShellContent`, i.e. of radix's
 * `Dialog.Portal` / vaul's `Drawer.Portal`, which render nothing while
 * closed: no `listOrders` request happens before the modal is opened, and the
 * subtree unmounts on close -- there is no per-open state here to reset
 * explicitly (unlike `RelayDepositBody`'s step/panel state), so
 * unmount-by-portal is the whole story.
 */
export function RelayHistoryContent({ className }: RelayHistoryContentProps) {
  const props = useRelayHistoryContext();
  const { open } = useRelayShellContext();

  React.useEffect(() => {
    if (open) trackDappUiFunnel("relay", "history_opened");
  }, [open]);

  return (
    <TrackingBoundary feature="relay">
      <ShellContent ariaTitle="Transfer history" className={className ?? props.className}>
        <RelayHistoryBody />
      </ShellContent>
    </TrackingBoundary>
  );
}

function RelayHistoryBody() {
  const props = useRelayHistoryContext();
  const { theme } = useRelayShellContext();

  // Resolution order: `client` wins; else the effective base URL --
  // `apiBaseUrl`, or `environment`'s built-in default map when `apiBaseUrl`
  // isn't passed -- builds one via createRelayClient (see
  // resolveRelayApiBaseUrl; always yields a URL, so there is no "neither
  // provided" throw case any more). Evaluated unconditionally (not gated on a
  // resolved recipient) since useRelayOrders always needs a client, even
  // while it isn't fetching anything yet.
  const client = React.useMemo(() => {
    if (props.client) return props.client;
    const baseUrl = resolveRelayApiBaseUrl({
      apiBaseUrl: props.apiBaseUrl,
      environment: props.environment,
    });
    return createRelayClient({ baseUrl });
  }, [props.client, props.apiBaseUrl, props.environment]);

  const resolvedRecipient = props.recipient ?? props.walletAddress;
  const hasValidRecipient = Boolean(resolvedRecipient && isAddress(resolvedRecipient));

  // History needs the catalog even though it has no token picker: amountIn,
  // amountOut and expectedOut are API smallest-unit strings. Without token
  // decimals they render as raw 18-digit integers and force the table to
  // overflow. Do not request config for the address-less empty state.
  const [catalog, setCatalog] = React.useState<Catalog>();
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [catalogError, setCatalogError] = React.useState<Error>();
  const onErrorRef = React.useRef(props.onError);
  onErrorRef.current = props.onError;

  React.useEffect(() => {
    if (!hasValidRecipient) {
      setCatalog(undefined);
      setCatalogLoading(false);
      setCatalogError(undefined);
      return;
    }

    let cancelled = false;
    setCatalog(undefined);
    setCatalogLoading(true);
    setCatalogError(undefined);

    client
      .getConfig()
      .then((result) => {
        if (!cancelled) setCatalog(result);
      })
      .catch((err) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setCatalogError(error);
        onErrorRef.current?.(error);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, hasValidRecipient]);

  const { orders, ordersLoading, ordersError } = useRelayOrders({
    client,
    // Invalid/absent resolved address: pass undefined through, exactly what
    // useRelayOrders treats as "clear the list, never fetch."
    recipient: hasValidRecipient ? resolvedRecipient : undefined,
    // Never paint raw smallest-unit amounts while config is still loading.
    enabled: Boolean(catalog),
    onError: props.onError,
  });

  // Notifies the host of a clicked history-row explorer link without
  // intercepting navigation (unlike dapp-ui's OnOutlink elsewhere, this is a
  // fire-and-forget notification -- the anchor's own target="_blank" still
  // opens the link). Delegated on the wrapper instead of threading a prop
  // into OrdersList, which renders its own explorer <a> tags.
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!props.onOutlink) return;
      const el = e.target as HTMLElement;
      const anchor = el.closest?.('a[target="_blank"]') as HTMLAnchorElement | null;
      if (anchor?.href) props.onOutlink(anchor.href);
    },
    [props.onOutlink],
  );

  return (
    <div className="rd-root rd-wizard" data-theme={theme}>
      {hasValidRecipient ? (
        <div onClick={handleClick}>
          <OrdersList
            orders={orders}
            isLoading={catalogLoading || ordersLoading}
            error={catalogError ?? ordersError}
            catalog={catalog}
            // ShellContent keeps an accessible, screen-reader-only title; the
            // table does not repeat it visually.
            showTitle={false}
          />
        </div>
      ) : (
        <HistoryEmptyState requiresIdentity />
      )}
    </div>
  );
}
